import {Component, OnInit} from '@angular/core';
import {FileSelectEvent, FileUploadModule} from "primeng/fileupload";
import {NgClass, NgIf, UpperCasePipe} from "@angular/common";
import {AvatarModule} from "primeng/avatar";
import {InputTextModule} from "primeng/inputtext";
import {FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from "@angular/forms";
import {TextareaModule} from "primeng/textarea";
import {Ripple} from "primeng/ripple";
import {Button} from "primeng/button";
import {DialogService, DynamicDialog, DynamicDialogRef} from "primeng/dynamicdialog";
import {ToastService} from "../../../core/services/toast.service";
import {InstitutionService} from "../../../core/services/institution.service";
import {getInstitutionTypeByValue, IInstitution, InstitutionType} from "../../../core/models/institution.model";
import {CustomDialogData} from "../../../core/models/custom-dialog.model";
import {SelectModule} from "primeng/select";
import {countries} from "../../../../assets/countries";
import {IModule} from "../../../core/models/module.model";
import {ModuleService} from "../../../core/services/module.service";
import {Direction, getUnpagedPageable, Sort} from "../../../core/models/page.model";
import {MultiSelectModule} from "primeng/multiselect";
import {AccordionModule} from "primeng/accordion";

@Component({
    selector: 'app-institution-details',
    imports: [
        // Modules
        FileUploadModule, AvatarModule, InputTextModule, ReactiveFormsModule, TextareaModule, MultiSelectModule,
        AccordionModule, SelectModule, Button,
        // Directives & Pipes
        NgIf, Ripple, UpperCasePipe,
        // Components
        NgClass,
    ],
    templateUrl: './institution-details.component.html',
    styleUrl: './institution-details.component.scss'
})
export class InstitutionDetailsComponent implements OnInit {

  formGroup = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(100)]
    }),
    type: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email]
    }),
    phone: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, this.phoneValidator.bind(this)]
    }),
    address: new FormControl('', {validators: [Validators.maxLength(200)]}),
    country: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    description: new FormControl('', {validators: [Validators.maxLength(500)]}),
    modules: new FormControl<string[]>([]),
  });
  uploadedFiles: File[] = [];
  isLoading: boolean = false;
  boundData: CustomDialogData | undefined;
  instance: DynamicDialog | undefined;
  isCreate: boolean = false;
  types: string[] = Object.values(InstitutionType);
  countries = countries;
  modules: IModule[] = [];
  productsDisabled: boolean = true;
  sort: Sort = { direction: Direction.DESC, orders: [{ property: 'code', ignoreCase: false }] };
  fileError: string = '';
  maxFileSize: number = 2000000; // 2MB
  allowedFileTypes: string[] = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  logoPreview: string | null = null;

  constructor(
    private service: InstitutionService,
    private moduleService: ModuleService,
    private toastService: ToastService,
    private dialogService: DialogService,
    private ref: DynamicDialogRef
  ) {
    this.instance = this.dialogService.getInstance(this.ref);
  }

  ngOnInit() {
    this.loadModules();
    this.boundData = this.instance?.data;
    const institution = this.boundData?.data as IInstitution;
    if (this.boundData?.isCreate) this.isCreate = true;
    if ((this.boundData?.isEdit || this.boundData?.isView) && this.boundData.data) {
      this.formGroup.patchValue(this.boundData.data);
      this.formGroup.patchValue({ modules: institution.modules.map(m => m.id) });
    }
    if (this.boundData?.isView) this.formGroup.disable();
  }

  onLogoUpdate($event: FileSelectEvent) {
    this.onUpload($event);
    this.service.updateLogo(this.boundData?.data.id, this.uploadedFiles[0]).subscribe({
      next: (institution) => {
        if (this.boundData) this.boundData.data = institution;
        this.toastService.showToast('Modification de logo', 'Le logo de l\'institution a été modifié avec succès.');
        this.ngOnInit();
      },
      error: (error) => {
        this.toastService.showToast('Modification du logo', error.error, 'error');
        this.ngOnInit();
      }
    });
  }

  onUpload(event: FileSelectEvent) {
    this.fileError = '';
    this.logoPreview = null;

    for(let file of event.currentFiles) {
      // Validation de la taille du fichier
      if (file.size > this.maxFileSize) {
        this.fileError = `Le fichier est trop volumineux. Taille maximale : ${this.maxFileSize / 1000000}MB`;
        this.uploadedFiles = [];
        this.logoPreview = null;
        this.toastService.showToast('Fichier invalide', this.fileError, 'error');
        return;
      }

      // Validation du type de fichier
      if (!this.allowedFileTypes.includes(file.type)) {
        this.fileError = 'Format de fichier non autorisé. Formats acceptés : JPG, PNG, GIF, WEBP';
        this.uploadedFiles = [];
        this.logoPreview = null;
        this.toastService.showToast('Fichier invalide', this.fileError, 'error');
        return;
      }

      this.uploadedFiles = [];
      this.uploadedFiles.push(file);

      // Générer une prévisualisation de l'image
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.logoPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  /**
   * Validateur personnalisé pour le téléphone
   */
  private phoneValidator(control: FormControl): ValidationErrors | null {
    if (!control.value) return null;

    const phoneStr = control.value.toString().trim();

    // Vérifier que le numéro ne contient que des chiffres, espaces, + et -
    if (!/^[\+\d\s\-]+$/.test(phoneStr)) {
      return { phoneInvalid: true };
    }

    // Extraire uniquement les chiffres
    const digitsOnly = phoneStr.replace(/[\s\-\+]/g, '');

    // Vérifier qu'il y a entre 8 et 15 chiffres
    if (digitsOnly.length < 8 || digitsOnly.length > 15) {
      return { phoneLength: true };
    }

    return null;
  }

  /**
   * Récupère le message d'erreur pour un champ donné
   */
  getFieldError(fieldName: string): string {
    const control = this.formGroup.get(fieldName);
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    const errors = control.errors;
    if (errors['required']) return 'Ce champ est requis';
    if (errors['email']) return 'Adresse email invalide';
    if (errors['minlength']) return `Minimum ${errors['minlength'].requiredLength} caractères`;
    if (errors['maxlength']) return `Maximum ${errors['maxlength'].requiredLength} caractères`;
    if (errors['phoneInvalid']) return 'Le numéro doit contenir uniquement des chiffres, espaces, + et -';
    if (errors['phoneLength']) return 'Le numéro doit contenir entre 8 et 15 chiffres';

    return 'Valeur invalide';
  }

  /**
   * Vérifie si un champ a une erreur et a été touché
   */
  hasFieldError(fieldName: string): boolean {
    const control = this.formGroup.get(fieldName);
    return !!(control && control.errors && control.touched);
  }

  /**
   * Marque tous les champs comme touchés pour afficher les erreurs
   */
  private markAllFieldsAsTouched(): void {
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      control?.markAsTouched();
    });
  }

  create() {
    // Validation du formulaire avant soumission
    if (this.formGroup.invalid) {
      this.markAllFieldsAsTouched();
      this.toastService.showToast(
        'Formulaire invalide',
        'Veuillez corriger les erreurs avant de soumettre le formulaire',
        'warn'
      );
      return;
    }

    // Validation du fichier logo (obligatoire lors de la création)
    if (!this.uploadedFiles || this.uploadedFiles.length === 0) {
      this.toastService.showToast(
        'Logo manquant',
        'Veuillez ajouter un logo pour l\'institution',
        'warn'
      );
      return;
    }

    this.isLoading = true;
    const institutionDto = this.formGroup.value as unknown as IInstitution;
    institutionDto.type = <string>getInstitutionTypeByValue(institutionDto.type);
    institutionDto.modules = this.modules.filter(m => this.formGroup.value.modules?.includes(m.id));

    this.service.createInstitution(institutionDto, this.uploadedFiles[0]).subscribe({
      next: (institution) => {
        this.toastService.showToast(
          'Institution créée',
          `L'organisation ${institution.name} a été créée avec succès.`
        );
        this.isLoading = false;
        this.ref.close();
      },
      error: (error) => {
        this.isLoading = false;
        this.handleHttpError(error, 'création');
      }
    });
  }

  update() {
    // Validation du formulaire avant soumission
    if (this.formGroup.invalid) {
      this.markAllFieldsAsTouched();
      this.toastService.showToast(
        'Formulaire invalide',
        'Veuillez corriger les erreurs avant de soumettre le formulaire',
        'warn'
      );
      return;
    }

    this.isLoading = true;
    const institutionDto = this.formGroup.value as unknown as IInstitution;
    institutionDto.type = <string>getInstitutionTypeByValue(institutionDto.type);

    this.service.updateInstitution(this.boundData?.data.id, institutionDto).subscribe({
      next: (institution) => {
        this.toastService.showToast(
          'Institution modifiée',
          `L'organisation ${institution.name} a été modifiée avec succès.`
        );
        this.isLoading = false;
        this.ref.close();
      },
      error: (error) => {
        this.isLoading = false;
        this.handleHttpError(error, 'modification');
      }
    });
  }

  toggleInstitution() {
    const enabled = this.boundData?.data.state == 'ACTIVE';
    this.service.toggleInstitution(this.boundData?.data.id).subscribe({
      next: () => {
        const header = enabled ? 'Institution désactivée' : 'Institution activée';
        const message = `L'institution a été ${enabled ? 'désactivée' : 'activée'} avec succès.`;
        this.toastService.showToast(header, message);
        this.ref.close();
      },
      error: (err) => {
        const action = enabled ? 'désactivation' : 'activation';
        this.handleHttpError(err, action);
      }
    });
  }

  /**
   * Gère les erreurs HTTP et affiche un message approprié
   */
  private handleHttpError(error: any, operation: string): void {
    let errorMessage = 'Une erreur est survenue. Veuillez réessayer.';

    if (error.error) {
      // Si l'erreur backend contient un message
      if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error.error.message) {
        errorMessage = error.error.message;
      } else if (error.error.error) {
        errorMessage = error.error.error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Messages d'erreur spécifiques selon le code HTTP
    if (error.status === 400) {
      errorMessage = 'Données invalides. ' + errorMessage;
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else if (error.status === 403) {
      errorMessage = 'Accès refusé. Vous n\'avez pas les permissions nécessaires.';
    } else if (error.status === 404) {
      errorMessage = 'Institution non trouvée.';
    } else if (error.status === 409) {
      errorMessage = 'Conflit. Une institution avec ces informations existe déjà.';
    } else if (error.status === 500) {
      errorMessage = 'Erreur serveur. Veuillez contacter l\'administrateur.';
    } else if (error.status === 0) {
      errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
    }

    this.toastService.showToast(
      `Erreur lors de la ${operation}`,
      errorMessage,
      'error'
    );
  }

  private loadModules() {
    this.moduleService.getAllModules(getUnpagedPageable(this.sort)).subscribe({
      next: (page) => this.modules = page.content,
      error: (err) => {
        this.toastService.showToast('Modules', err.error, 'error');
        this.ref.close();
      }
    });
  }
}
